import { isValidAnnouncementUrl } from '@/announcement/announcement'
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  UseModalProps,
  VStack,
} from '@chakra-ui/react'
import { AnnouncementFragment } from '@gql'
import { AnnouncementType } from '@rolebase/shared/model/release'
import { getDateTimeLocal } from '@utils/dates'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

export interface AnnouncementValues {
  type: AnnouncementType
  message: string
  startsAt: string
  endsAt: string
  url: string | null
}

interface Props extends UseModalProps {
  // Absent: a new announcement
  announcement?: AnnouncementFragment
  onSubmit(values: AnnouncementValues): Promise<void>
}

const types: AnnouncementType[] = ['upcoming', 'maintenance']

// #77 — write or change an announcement. Its banner shows as soon as it is
// saved and leaves by itself at the planned end.
export default function AdminAnnouncementModal({
  announcement,
  onSubmit,
  ...modalProps
}: Props) {
  const { t } = useTranslation()
  const [type, setType] = useState<AnnouncementType>(
    (announcement?.type as AnnouncementType) ?? 'upcoming'
  )
  const [message, setMessage] = useState(announcement?.message ?? '')
  const [startsAt, setStartsAt] = useState(
    announcement ? getDateTimeLocal(new Date(announcement.startsAt)) : ''
  )
  const [endsAt, setEndsAt] = useState(
    announcement ? getDateTimeLocal(new Date(announcement.endsAt)) : ''
  )
  const [url, setUrl] = useState(announcement?.url ?? '')
  const [loading, setLoading] = useState(false)

  const datesInvalid =
    !!startsAt && !!endsAt && new Date(endsAt) <= new Date(startsAt)
  const urlInvalid = !isValidAnnouncementUrl(url)
  const canSubmit =
    message.trim() !== '' &&
    !!startsAt &&
    !!endsAt &&
    !datesInvalid &&
    !urlInvalid

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    setLoading(true)
    try {
      await onSubmit({
        type,
        message: message.trim(),
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        url: url.trim() || null,
      })
      modalProps.onClose()
    } catch {
      // The page already showed the error: the window stays open to retry
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal size="lg" {...modalProps}>
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit}>
        <ModalHeader>
          {announcement
            ? t('SuperAdmin.announcements.modal.editHeading')
            : t('SuperAdmin.announcements.modal.newHeading')}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={5} align="stretch">
            <FormControl>
              <FormLabel>{t('SuperAdmin.announcements.modal.type')}</FormLabel>
              <Select
                value={type}
                onChange={(e) => setType(e.target.value as AnnouncementType)}
              >
                {types.map((value) => (
                  <option key={value} value={value}>
                    {t(`AnnouncementBanner.types.${value}`)}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>
                {t('SuperAdmin.announcements.modal.message')}
              </FormLabel>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
              />
              <FormHelperText>
                {t('SuperAdmin.announcements.modal.messageHelp')}
              </FormHelperText>
            </FormControl>

            <FormControl isRequired isInvalid={datesInvalid}>
              <HStack align="start" spacing={4} flexWrap="wrap">
                <FormControl isRequired w="auto">
                  <FormLabel>
                    {t('SuperAdmin.announcements.modal.startsAt')}
                  </FormLabel>
                  <Input
                    type="datetime-local"
                    value={startsAt}
                    onChange={(e) => setStartsAt(e.target.value)}
                    w="230px"
                    maxW="100%"
                  />
                </FormControl>
                <FormControl isRequired w="auto">
                  <FormLabel>
                    {t('SuperAdmin.announcements.modal.endsAt')}
                  </FormLabel>
                  <Input
                    type="datetime-local"
                    value={endsAt}
                    onChange={(e) => setEndsAt(e.target.value)}
                    w="230px"
                    maxW="100%"
                  />
                </FormControl>
              </HStack>
              <FormErrorMessage>
                {t('SuperAdmin.announcements.modal.datesInvalid')}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={urlInvalid}>
              <FormLabel>{t('SuperAdmin.announcements.modal.url')}</FormLabel>
              <Input
                type="url"
                placeholder="https://…"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
              <FormErrorMessage>
                {t('SuperAdmin.announcements.modal.urlInvalid')}
              </FormErrorMessage>
            </FormControl>

            {/* Hors de tout FormControl : un Text, jamais un FormHelperText,
                qui planterait toute la fenêtre (même piège qu'AdminOrgPlanModal). */}
            {!announcement && (
              <Text fontSize="sm" color="gray.500">
                {t('SuperAdmin.announcements.modal.publishHelp')}
              </Text>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button mr={3} onClick={modalProps.onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            colorScheme="blue"
            isDisabled={!canSubmit}
            isLoading={loading}
          >
            {announcement
              ? t('common.save')
              : t('SuperAdmin.announcements.modal.publish')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
